import React, { useState, useEffect, useRef, useMemo } from 'react';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton,
    Alert, Chip, TextField, Button, InputAdornment,
} from '@mui/material';
import {
    Close, DragIndicator, Search as SearchIcon, Clear as ClearIcon, LowPriority,
} from '@mui/icons-material';
import {
    DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { businessAPI } from '../api';
import { sortCustomersInArea } from '../utils/routeOrder';

// How many houses are draggable at once. Every rendered row subscribes to dnd-kit's context and
// re-renders on each pointer move, and collision detection measures all of them — so a round of
// 900 houses cannot put every row on screen and still drag. A window keeps that work constant
// while the full order below stays the source of truth.
const PAGE_SIZE = 50;

const rowSubtitle = (c) => [c.f_mobile, c.f_address_line1].filter(Boolean).join(' · ');

// Drag lives on the grip, never on the whole row: a draggable row would have to allow panning so
// the list could still be scrolled, and the browser then claims a vertical swipe as a scroll
// before the drag starts. `touch-action: none` on the grip alone keeps both gestures working.
const SortableRow = ({ customer, position, onPickPosition }) => {
    const {
        attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging,
    } = useSortable({ id: customer.f_id });
    return (
        <Box ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                borderBottom: '1px solid', borderColor: 'divider',
                bgcolor: isDragging ? 'primary.50' : 'background.paper',
                userSelect: 'none',
            }}>
            <Box ref={setActivatorNodeRef} {...attributes} {...listeners}
                sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 44, minHeight: 52, flexShrink: 0,
                    touchAction: 'none',
                    color: 'text.disabled',
                    cursor: 'grab', '&:active': { cursor: 'grabbing' },
                }}>
                <DragIndicator />
            </Box>
            <RowBody customer={customer} position={position} onPickPosition={onPickPosition} />
        </Box>
    );
};

// The same row without drag, used while a search is active — the matches on screen are not each
// other's real neighbours, so dropping one between two of them would place it somewhere else.
const PlainRow = ({ customer, position, onPickPosition }) => (
    <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, pl: 1.5,
        borderBottom: '1px solid', borderColor: 'divider',
    }}>
        <RowBody customer={customer} position={position} onPickPosition={onPickPosition} />
    </Box>
);

const RowBody = ({ customer, position, onPickPosition }) => (
    <>
        <Chip label={position} size="small"
            sx={{ width: 40, height: 24, fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0, py: 1.25 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{customer.f_name}</Typography>
            {rowSubtitle(customer) && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {rowSubtitle(customer)}
                </Typography>
            )}
        </Box>
        <IconButton size="small" title="Move to a position"
            onClick={() => onPickPosition(customer)}
            sx={{ flexShrink: 0, mr: 1 }}>
            <LowPriority fontSize="small" />
        </IconButton>
    </>
);

/**
 * Set the order houses are delivered to within one area (or the customers with no area).
 *
 * `customers` must be every customer in that group. Only a window of them is draggable at a
 * time, but every move is resolved against the complete order, so the two neighbours sent to
 * the server are the real ones even at a page boundary.
 */
export default function DeliveryOrderDialog({ open, onClose, businessId, title, customers = [] }) {
    const [list, setList] = useState([]);       // the full round, in order — source of truth
    const [search, setSearch] = useState('');
    const [pageStart, setPageStart] = useState(0);
    const [posTarget, setPosTarget] = useState(null);   // customer whose position is being typed
    const [posValue, setPosValue] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const changedRef = useRef(false);

    const sensors = useSensors(
        // Both start on a small movement. No press-and-hold delay is needed now that the grip is
        // the only drag target — there is no scroll gesture left to disambiguate from.
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { distance: 8 } }),
    );

    useEffect(() => {
        if (!open) return;
        setList(sortCustomersInArea(customers));
        setSearch('');
        setPageStart(0);
        setPosTarget(null);
        setError('');
        changedRef.current = false;
    }, [open, customers]);

    const q = search.trim().toLowerCase();
    const matches = useMemo(() => {
        if (!q) return null;
        return list
            .map((c, i) => ({ c, i }))
            .filter(({ c }) => c.f_name?.toLowerCase().includes(q) || c.f_mobile?.includes(q));
    }, [list, q]);

    const total = list.length;
    const windowRows = useMemo(
        () => list.slice(pageStart, pageStart + PAGE_SIZE),
        [list, pageStart]
    );

    /** Move the customer at `fromIdx` to `toIdx` in the full list and persist it. */
    const applyMove = async (fromIdx, toIdx) => {
        if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
        const previous = list;
        const id = previous[fromIdx].f_id;
        const moved = arrayMove(previous, fromIdx, toIdx);
        setList(moved);            // optimistic — the drop should feel instant
        setError('');
        setSaving(true);
        try {
            // Neighbours come from the complete order, so this is correct even when the row was
            // dropped at the very top or bottom of the visible window.
            const res = await businessAPI.moveCustomer(
                businessId, id,
                moved[toIdx - 1]?.f_id ?? null,
                moved[toIdx + 1]?.f_id ?? null,
            );
            changedRef.current = true;
            // A respace rewrites every rank in the group. The visible order does not change, but
            // local ranks must follow or the next move computes its neighbours from stale values.
            const { f_sort_order, renumbered, order } = res.data || {};
            setList(cur => cur.map(c => {
                if (renumbered && order && order[c.f_id] != null) {
                    return { ...c, f_sort_order: c.f_id === id ? f_sort_order : order[c.f_id] };
                }
                return c.f_id === id ? { ...c, f_sort_order } : c;
            }));
        } catch (e) {
            // Never fail silently: a route the vendor believes they saved is a wrong delivery
            // round the next morning.
            setList(previous);
            setError(e?.response?.data?.detail || 'Could not save the new order. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        applyMove(
            list.findIndex(c => c.f_id === active.id),
            list.findIndex(c => c.f_id === over.id),
        );
    };

    const submitPosition = () => {
        const n = parseInt(posValue, 10);
        if (!Number.isInteger(n) || n < 1 || n > total) {
            setError(`Enter a position between 1 and ${total}.`);
            return;
        }
        const from = list.findIndex(c => c.f_id === posTarget.f_id);
        setPosTarget(null);
        setPosValue('');
        applyMove(from, n - 1);
        // Follow the customer to where they landed.
        setSearch('');
        setPageStart(Math.max(0, Math.floor((n - 1) / PAGE_SIZE) * PAGE_SIZE));
    };

    const handleClose = () => {
        setError('');
        onClose(changedRef.current);
    };

    const pickPosition = (c) => {
        setPosTarget(c);
        setPosValue(String(list.findIndex(x => x.f_id === c.f_id) + 1));
        setError('');
    };

    const showPager = !q && total > PAGE_SIZE;

    return (
        <Dialog open={open} onClose={handleClose} {...APP_DIALOG}
            PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>
            <DialogTitle sx={APP_DIALOG_TITLE_SX}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" noWrap>Delivery Order</Typography>
                        <Typography variant="caption" noWrap>
                            {title}{total ? ` · ${total} customers` : ''}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}><Close /></IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {error && (
                    <Alert severity="error" sx={{ m: 1.5 }} onClose={() => setError('')}>{error}</Alert>
                )}

                {total === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
                        <Typography variant="body2" color="text.secondary">No customers here yet.</Typography>
                        <Typography variant="caption" color="text.disabled">
                            Add customers to this area first, then set the order they are delivered to.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {total > PAGE_SIZE && (
                            <Box sx={{ px: 1.5, pt: 1.5 }}>
                                <TextField
                                    size="small" fullWidth
                                    placeholder="Find a customer by name or mobile"
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" sx={{ color: search ? 'primary.main' : 'text.disabled' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: search ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.5 }}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: 'background.paper' } }}
                                />
                            </Box>
                        )}

                        <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', px: 2, py: 1.25 }}>
                            {q
                                ? `${matches.length} match${matches.length === 1 ? '' : 'es'}. Use the position button to move one — clear the search to drag.`
                                : 'Drag the handle to reorder, or use the position button to send someone straight to a number. 1 is the first house.'}
                        </Typography>

                        <Box sx={{ opacity: saving ? 0.6 : 1, transition: 'opacity 120ms' }}>
                            {q ? (
                                matches.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
                                        No customer matches that.
                                    </Typography>
                                ) : (
                                    matches.slice(0, 100).map(({ c, i }) => (
                                        <PlainRow key={c.f_id} customer={c} position={i + 1} onPickPosition={pickPosition} />
                                    ))
                                )
                            ) : (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={windowRows.map(c => c.f_id)} strategy={verticalListSortingStrategy}>
                                        {windowRows.map((c, i) => (
                                            <SortableRow key={c.f_id} customer={c}
                                                position={pageStart + i + 1} onPickPosition={pickPosition} />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            )}
                        </Box>

                        {showPager && (
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: 1, px: 2, py: 1.5,
                            }}>
                                <Button size="small" disabled={pageStart === 0}
                                    onClick={() => setPageStart(s => Math.max(0, s - PAGE_SIZE))}
                                    sx={{ textTransform: 'none' }}>
                                    ‹ Prev
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                    {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, total)} of {total}
                                </Typography>
                                <Button size="small" disabled={pageStart + PAGE_SIZE >= total}
                                    onClick={() => setPageStart(s => Math.min(total - 1, s + PAGE_SIZE))}
                                    sx={{ textTransform: 'none' }}>
                                    Next ›
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            {/* Send a customer straight to a position — the practical way to move someone a long
                way without dragging past hundreds of rows. */}
            <Dialog open={posTarget !== null} onClose={() => setPosTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>{posTarget?.f_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Deliver at which position?
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth size="small" type="number"
                        value={posValue}
                        onChange={e => setPosValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') submitPosition(); }}
                        inputProps={{ min: 1, max: total, inputMode: 'numeric' }}
                        helperText={`1 = first house, ${total} = last`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPosTarget(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={submitPosition}
                        sx={{ textTransform: 'none' }}>
                        Move
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
